import React from "react";
import { Link } from "react-router-dom";
import { LEGAL, COMPLIANCE_BLOCK, FACILITATION_DISCLOSURE, NON_ADVICE_LINE } from "../../lib/legalConfig";
import { Brand, BrandMonogram } from "../Brand";

/**
 * Legitimacy footer — appears on every marketing and legal page. The
 * "compliance block" (`COMPLIANCE_BLOCK`) is the lawyer-approved sentence
 * we MUST surface verbatim on every public surface. The non-advice line
 * (`NON_ADVICE_LINE`) is required on the footer and on receipts.
 */
export default function LegalFooter() {
  return (
    <footer
      className="border-t border-border bg-muted/40 mt-16 relative overflow-hidden"
      data-testid="legal-footer"
    >
      {/* Subtle branded watermark in the corner */}
      <div
        className="absolute right-[-30px] bottom-[-30px] pointer-events-none select-none opacity-[0.06]"
        aria-hidden
      >
        <BrandMonogram size={220} />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <Brand size={28} theme="light" />
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              A virtual-currency on-ramp.
            </p>
          </div>

          <FooterCol title="Legal">
            <FooterLink to="/legal/terms" testid="footer-terms">Terms of Service</FooterLink>
            <FooterLink to="/legal/privacy" testid="footer-privacy">Privacy Policy</FooterLink>
            <FooterLink to="/legal/aml" testid="footer-aml">AML / KYC Disclosure</FooterLink>
            <FooterLink to="/legal/why-sof" testid="footer-why-sof">Why we ask for Source of Funds</FooterLink>
            <FooterLink to="/legal/cookies" testid="footer-cookies">Cookie Notice</FooterLink>
          </FooterCol>

          <FooterCol title="Support">
            <FooterLink to="/support" testid="footer-support">Help &amp; Support</FooterLink>
            <FooterExt
              href={`mailto:${LEGAL.SUPPORT_EMAIL}`}
              testid="footer-support-email"
            >
              {LEGAL.SUPPORT_EMAIL}
            </FooterExt>
          </FooterCol>

        </div>

        {/* Lawyer-approved compliance block — verbatim, single source of truth */}
        <div
          className="border-t border-border mt-8 pt-5 text-[11px] text-muted-foreground leading-relaxed"
          data-testid="footer-compliance-block"
        >
          <p>{COMPLIANCE_BLOCK}</p>
          <p className="mt-1.5" data-testid="footer-non-advice-line">
            {FACILITATION_DISCLOSURE} {NON_ADVICE_LINE}
          </p>
        </div>

        <div className="mt-4 text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} Xchangely. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

const FooterCol = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-3">
      {title}
    </div>
    <ul className="space-y-2">{children}</ul>
  </div>
);
const FooterLink = ({ to, children, testid }) => (
  <li>
    <Link
      to={to}
      className="text-xs text-foreground/80 hover:text-foreground transition-colors"
      data-testid={testid}
    >
      {children}
    </Link>
  </li>
);
const FooterExt = ({ href, children, testid }) => (
  <li>
    <a
      href={href}
      className="text-xs text-foreground/80 hover:text-foreground transition-colors"
      data-testid={testid}
    >
      {children}
    </a>
  </li>
);
